import React from 'react';
import { toast } from 'sonner';
import { 
  Settings, ToggleLeft, ToggleRight, RefreshCw, 
  Zap, Shield, ArrowLeft
} from 'lucide-react';
import {
  useAllFeatureToggles,
  setFeatureToggle,
  resetFeatureToggles,
  enableAllNewComponents,
  disableAllNewComponents,
  FEATURE_TOGGLES
} from '../featureToggles';


const TOGGLE_INFO = {
  USE_NEW_REPORTS_VIEW: {
    name: 'Reports View',
    description: 'Use new modular ReportsView component for AI report generation',
    component: 'ReportsView.js',
    risk: 'low'
  },
  USE_NEW_CONSULTATION_VIEW: {
    name: 'Consultation View',
    description: 'Use new modular ConsultationView for chat sessions',
    component: 'ConsultationView.js',
    risk: 'medium'
  },
  USE_NEW_PAYMENT_VIEW: {
    name: 'Payment View',
    description: 'Use new modular PaymentView for pass purchases',
    component: 'PaymentView.js',
    risk: 'high'
  },
  USE_NEW_TRIAL_VIEW: {
    name: 'Free Trial View',
    description: 'Use new modular FreeTrialView for trial users',
    component: 'FreeTrialView.js',
    risk: 'low'
  },
  USE_NEW_HEALTH_VIEW: {
    name: 'Health View',
    description: 'Use new modular HealthView for health tracking',
    component: 'HealthView.js',
    risk: 'low'
  },
  USE_ALL_NEW_COMPONENTS: {
    name: 'Master Switch',
    description: 'Enable ALL new components at once (overrides individual toggles)',
    component: 'All components',
    risk: 'high'
  }
};

const RiskBadge = ({ risk }) => {
  const colors = {
    low: 'bg-green-500/20 text-green-400',
    medium: 'bg-amber-500/20 text-amber-400',
    high: 'bg-red-500/20 text-red-400'
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[risk]}`}>
      {risk.toUpperCase()} RISK
    </span>
  );
};

const ToggleSwitch = ({ enabled, onChange, disabled = false }) => (
  <button
    onClick={() => onChange(!enabled)}
    disabled={disabled}
    className={`
      relative w-14 h-7 rounded-full transition-all duration-300
      ${enabled ? 'bg-green-500' : 'bg-slate-600'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'}
    `}
  >
    <div className={`
      absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md
      transition-transform duration-300
      ${enabled ? 'translate-x-7' : 'translate-x-0.5'}
    `}>
      {enabled ? (
        <ToggleRight className="w-4 h-4 text-green-500 m-1" />
      ) : (
        <ToggleLeft className="w-4 h-4 text-slate-400 m-1" />
      )}
    </div>
  </button>
);

const FeatureToggleAdmin = ({ onBack }) => {
  const toggles = useAllFeatureToggles();
  
  const handleToggleChange = (toggleName, value) => {
    setFeatureToggle(toggleName, value);
    toast.success(`${TOGGLE_INFO[toggleName]?.name || toggleName} ${value ? 'enabled' : 'disabled'}`);
  };
  
  const handleEnableAll = () => {
    enableAllNewComponents();
    toast.success('All new components enabled');
  };
  
  const handleDisableAll = () => {
    disableAllNewComponents();
    toast.success('All new components disabled (using original)');
  };
  
  const handleReset = () => {
    resetFeatureToggles();
    toast.success('All toggles reset to defaults');
    window.location.reload();
  };
  
  const enabledCount = Object.values(toggles).filter(Boolean).length;
  const totalCount = Object.keys(toggles).length;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        {onBack && (
          <button onClick={onBack} className="text-white">
            <ArrowLeft className="w-6 h-6" />
          </button>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Settings className="w-6 h-6 text-purple-400" />
            Feature Toggles
          </h1>
          <p className="text-gray-400 text-sm">
            A/B testing for Sankat Mochan component migration
          </p>
        </div>
        <div className="bg-slate-800/50 px-4 py-2 rounded-lg">
          <span className="text-gray-400 text-sm">Active: </span>
          <span className="text-green-400 font-bold">{enabledCount}/{totalCount}</span>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button
          onClick={handleEnableAll}
          className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-xl hover:bg-green-500/30 transition-all"
        >
          <Zap className="w-4 h-4" />
          Enable All New Components
        </button>
        <button
          onClick={handleDisableAll}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 text-amber-400 rounded-xl hover:bg-amber-500/30 transition-all"
        >
          <Shield className="w-4 h-4" />
          Use Original (Safe Mode)
        </button>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-gray-300 rounded-xl hover:bg-slate-600 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Reset to Defaults
        </button>
      </div>
      
      {/* Toggle Cards */}
      <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
        {Object.entries(FEATURE_TOGGLES).map(([key, toggleName]) => {
          const info = TOGGLE_INFO[toggleName] || { name: toggleName, description: '', risk: 'medium' };
          const isEnabled = toggles[toggleName];
          const isMasterSwitch = toggleName === 'USE_ALL_NEW_COMPONENTS';
          
          return (
            <div
              key={key}
              className={`
                bg-slate-800/50 rounded-xl p-5 border transition-all
                ${isEnabled ? 'border-green-500/50' : 'border-slate-700'}
                ${isMasterSwitch ? 'md:col-span-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10' : ''}
              `}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-white font-bold flex items-center gap-2">
                    {info.name}
                    {isMasterSwitch && <Zap className="w-4 h-4 text-amber-400" />}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">{info.description}</p>
                </div>
                <ToggleSwitch
                  enabled={isEnabled}
                  onChange={(value) => handleToggleChange(toggleName, value)}
                />
              </div>
              
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-700">
                <code className="text-xs text-purple-400 bg-purple-500/10 px-2 py-1 rounded">
                  {info.component}
                </code>
                <RiskBadge risk={info.risk} />
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Instructions */}
      <div className="mt-8 max-w-4xl bg-slate-800/30 rounded-xl p-5 border border-slate-700">
        <h3 className="text-white font-bold mb-3">How to Test</h3>
        <div className="space-y-2 text-gray-400 text-sm">
          <p><code className="text-cyan-400">?feature_reports=true</code> - Enable Reports via URL</p>
          <p><code className="text-cyan-400">?feature_consultation=true</code> - Enable Consultation via URL</p>
          <p><code className="text-cyan-400">localStorage.setItem('SM_FEATURE_USE_NEW_REPORTS_VIEW', 'true')</code> - Enable via console</p>
        </div>
      </div>
    </div>
  );
};

export default FeatureToggleAdmin;
